import { type RedirectTo } from "@client/content/lib/settings";
import { resolvePageUrl, upsertQueryString } from "@client/lib/api";
import { goTo } from "@client/lib/navigation";
import { pairs } from "@client/lib/object";
import { activePopupSelector, apiInfoSelector } from "@client/selectors";
import { type EntityRef, type GenericMap, type LoadSpaPageArgs, ScreenType } from "@client/types";
import { type AppState, type BaseAppState } from "@client/types/AppState";
import { type ViewportAppState, type ViewportAppStateBase } from "@client/types/AppState/viewport";
import { isUndefined } from "lodash";
import { type Action, type Dispatch } from "redux";
import { type PopupState } from "./types/AppState/popups";

export enum Actions {
  InitBegin = "INIT_BEGIN",
  InitFailure = "INIT_FAILURE",
  InitSuccess = "INIT_SUCCESS",

  LoadPageBegin = "LOAD_PAGE_BEGIN",
  LoadPageFailure = "LOAD_PAGE_FAILURE",
  LoadPageSuccess = "LOAD_PAGE_SUCCESS",
  LoadPageRedirect = "LOAD_PAGE_REDIRECT",

  SsrInit = "SSR_INIT" /* after the app is initialized from the preloaded state */,

  // Module can report if it's loading (pending to display the content).
  // We use it in e.g. viewer, if the consumer wants to know when all modules are ready to display
  ReportModuleLoading = "REPORT_MODULE_LOADING",
  ResetModuleReducerState = "RESET_MODULE_REDUCER_STATE",

  SetModulePreview = "SET_MODULE_PREVIEW",
  SetPopupState = "SET_POPUP_STATE",

  SetEntities = "SET_ENTITIES",
  SetStyleSheet = "SET_STYLE_SHEET",
  SetViewport = "SET_VIEWPORT",

  SetActiveEventId = "SET_EVENT_ID"
}

export type RedirectQsItem = {
  key: string;
  value: any;
};
export type RedirectQs = GenericMap<RedirectQsItem | string | number | boolean>;
export type TriggerRedirectArgs = {
  fallbackUrl?: string;
  isOptIn?: boolean;
  qs?: RedirectQs;
  reload?: boolean;
  replace?: boolean;
  orderId?: string;
};
export type TriggerRedirectActionData = TriggerRedirectArgs & {
  redirectTo?: RedirectTo;
};
export const redirect = (args: TriggerRedirectActionData) => (dispatch: any, getState: () => BaseAppState) => {
  const { redirectTo, ...data } = args as TriggerRedirectActionData;

  const getLoadSpaPageArgs = (
    redirectTo: RedirectTo,
    args: TriggerRedirectArgs,
    pageVariantId: string
  ): LoadSpaPageArgs | null => {
    if ((isUndefined(redirectTo.type) || redirectTo.type === "url" || redirectTo.type === "lightbox") && redirectTo.url)
      return { path: redirectTo.url! };
    if ((!redirectTo || redirectTo.type === "step") && redirectTo.funnelPathId) {
      return {
        path: `/funnelPath/${redirectTo.funnelPathId}/next?pvid=${pageVariantId}&oid=${args.orderId ?? ""}&isOptIn=${
          args.isOptIn ?? false
        }`
      };
    }
    return redirectTo.pageId ? { pageId: redirectTo.pageId } : null;
  };

  const withFallback = async (fn: () => Promise<boolean>, fallbackUrl?: string) => {
    const isRedirected = await fn();
    if (!isRedirected && fallbackUrl) {
      window.location.assign(fallbackUrl);
    }
  };

  withFallback(async () => {
    if (!redirectTo) return false;

    if (redirectTo.popupContentId) {
      const popup = getState().popups.find((x) => x.contentId === redirectTo.popupContentId);

      popup && dispatch(openPopup(popup.id));
      return true;
    }

    const state = getState();

    const loadPageArgs = getLoadSpaPageArgs(redirectTo, data, state.page.variantId);
    if (!loadPageArgs) return false;

    // if we don't have the redirect path, pull it by the page Id
    if (!loadPageArgs.path) {
      const apiInfo = apiInfoSelector(state);
      loadPageArgs.path = await resolvePageUrl(apiInfo.url, loadPageArgs.pageId!, {
        cultureCode: state.cultureCode
      });
    }

    if (!loadPageArgs.path) return false; // still no path - do nothing

    if (redirectTo.qs?.length && data.qs) {
      const qsPairs = pairs(data.qs);

      // NOTE: UPSERT
      loadPageArgs.path = upsertQueryString(
        loadPageArgs.path,
        redirectTo.qs.reduce((res, x) => {
          let v = qsPairs.find((y) => y.key.toLowerCase() === x.toLowerCase());
          if (!v) {
            // backward compatibility for keys
            v = qsPairs.find((y) => {
              if (typeof y.value === "object") {
                return y.value.key.toLowerCase() === x.toLowerCase();
              }
              return false;
            });
            if (!v) return res;
          }

          const qv: RedirectQsItem = typeof v.value === "object" ? v.value : { key: x, value: v.value };
          return {
            ...res,
            [qv.key]: qv.value
          };
        }, {})
      );
    }

    if (redirectTo.newTab) {
      window.open(loadPageArgs.path, "_blank");
    } else if (data.reload) {
      window.location.assign(loadPageArgs.path);
    } else {
      goTo(loadPageArgs.path, data.replace);
    }

    return true;
  }, data.fallbackUrl);
};

export const redirectToPage = (pageId: string, newTab?: boolean) =>
  redirect({
    redirectTo: {
      pageId,
      newTab: newTab ?? false,
      type: "tab"
    }
  });

export type ReportModuleLoadingData = {
  moduleId: string;
  isLoading: boolean;
};
export const reportModuleLoading = (data: ReportModuleLoadingData) => ({
  type: Actions.ReportModuleLoading,
  data
});

export type SetPopupStateActionData = OpenPopupOpts & {
  id: string;
};
export const setPopupState = (data: SetPopupStateActionData) => ({
  type: Actions.SetPopupState,
  data
});

type OpenPopupOpts = {
  state: PopupState;
  triggerEntityRef?: EntityRef | null;
};
export const openPopup = (id: string, opts?: OpenPopupOpts) => (dispatch: any, getState: () => BaseAppState) => {
  const state = getState();
  const activePopup = activePopupSelector(state);

  if (activePopup?.id === id) return;

  dispatch(setPopupState({ id, state: opts?.state ?? "open", triggerEntityRef: opts?.triggerEntityRef }));
};

export const closeActivePopup = () => (dispatch: any, getState: () => BaseAppState) => {
  const state = getState();
  const activePopup = activePopupSelector(state);
  if (!activePopup) return;

  dispatch(setPopupState({ id: activePopup.id, state: "closed" }));
};

export interface ClassNamesUpdateAction extends Action {
  type: Actions;
  toAdd: string[];
  toRemove: string[];
}

export type SetViewportActionData = {
  fixedScreen?: ScreenType | null;
  viewport: ViewportAppState;
};
export const setViewport =
  (data: ViewportAppStateBase, fixedScreen?: ScreenType | null) =>
  (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
    dispatch({
      type: Actions.SetViewport,
      data: <SetViewportActionData>{
        fixedScreen,
        viewport: data
      }
    });
  };

export const showEventSelectorPopup = (cannotClose: boolean) => (dispatch: Dispatch<any>, getState: () => AppState) => {
  // TODO_EXPOMARKET
  // dispatch(openPopup(globals.siteInfo.systemPopupIds.eventPopupId, { state: cannotClose ? "openFixed" : "open" }));
};

export type SetActiveEventIdActionData = {
  id: string | null;
};
export const setActiveEventId = (id: string | null) => ({
  type: Actions.SetActiveEventId,
  data: {
    id
  }
});

export const resetModuleReducerState = () => ({
  type: Actions.ResetModuleReducerState
});
